import { icmPressureBucket, riskPremium } from '../strategy/icm';
import { createRng } from '../core/rng';
import { effectiveStackBB, levelAt, mRatio } from './blinds';
import { bubbleFactor } from './icm';
import { isPushFoldDepth, mZone, nearestPushFoldBand, stageForStack, } from './staging';
/** Hero is ALWAYS at index 0 of `stacks`, and is never removed by the field model, so the index is
 *  stable across busts (survivors are filtered but hero keeps position 0). */
export const HERO_INDEX = 0;
/** The alive-field size at or below which ICM is priced EXACTLY off the running stacks. ICM pressure is
 *  a final-table / bubble phenomenon (this structure's money bubble IS a 9-handed table), and the exact
 *  ICM solver (`calculateExact`, a subset-DP) is only fast at small counts - benchmarked ~0.5ms at 9
 *  and ~2ms at 13, then climbing steeply. 13 covers the final table + a two-table bubble while staying
 *  sub-2ms per solve, and matches the engine's own priceable snapshots (bubble=9/itm=8/finalTable=6, all
 *  <= 13). Above it, the honest answer is chip-EV (pressure `none`) - deep-field play is chip-EV, exactly
 *  as ST2's `isIcmPriceable` already declares for the sampled early/middle snapshots. */
export const ICM_PRICEABLE_MAX = 13;
/** Blind levels advance every this many HERO hands - a deterministic clock proxy (a real level is on a
 *  wall-clock timer; hero plays ~this many hands per level). No `Date`/RNG: the clock is `handNo`. */
export const HANDS_PER_LEVEL = 8;
// ── Elimination-model tuning (named + documented so the curve cannot silently drift) ────────────────
// Per HERO hand, many hands are played field-wide (every other table is dealing too). We model each
// surviving villain's hazard of busting during that interval as a function of their M-pressure, plus a
// small floor so the deep early field still sheds the occasional cooler/gambler. Tuned (see field.test)
// so a 60-runner local daily shrinks realistically - front-loaded early churn slowing to a grind near
// the money - and reaches a final table and heads-up.
/** Baseline per-interval bust hazard scale for a stack under real blind pressure. */
const HAZARD_SCALE = 0.16;
/** The cooler floor: even a deep, healthy stack busts with at least this hazard per interval (a set-over-
 *  set, a gamble). Keeps the early field shrinking honestly instead of freezing at 60. */
const COOLER_FLOOR = 0.012;
/** Bubble stall: within this many players ABOVE the money, hazard is damped (bubble play tightens up -
 *  nobody wants to bust one off the cash), so the bubble grinds rather than collapses. */
const BUBBLE_STALL_WINDOW = 3;
const BUBBLE_STALL_FACTOR = 0.45;
/**
 * A single villain's bust hazard for one hero-hand interval. Driven by Harrington M: a short stack
 * (low M) is in the push/fold danger zone and busts often; a deep stack rarely. `1/(1+(M/5)^2)` maps
 * M=0 → 1, M=5 → 0.5, M=10 → ~0.2, M=20 → ~0.06, then the cooler floor sets the deep minimum.
 */
const villainHazard = (chips, level) => {
    const m = mRatio(chips, level);
    const short = 1 / (1 + (m / 5) ** 2);
    return Math.min(0.95, Math.max(COOLER_FLOOR, HAZARD_SCALE * short + COOLER_FLOOR));
};
/** Boot a fresh field: `fieldSize` equal starting stacks, hero at index 0, at the start level. */
export const initField = (inp) => {
    const { structure } = inp;
    if (structure.fieldSize < 2)
        throw new RangeError(`fieldSize must be >= 2, got ${structure.fieldSize}`);
    const stacks = new Array(structure.fieldSize).fill(inp.startingChips);
    return {
        seed: inp.seed,
        structureId: structure.id,
        scheduleId: inp.schedule.id,
        startingChips: inp.startingChips,
        fieldSize: structure.fieldSize,
        paidPlaces: structure.paidPlaces,
        stacks,
        level: inp.startLevel ?? 1,
        handNo: 0,
    };
};
// ── Derived reads (all off the live running stacks) ─────────────────────────────────────────────────
/** Players still alive in the field. */
export const aliveCount = (s) => s.stacks.length;
/** Hero's chips right now. */
export const heroChips = (s) => s.stacks[HERO_INDEX];
/** Hero's chip rank in the whole live field (1 = chip leader). */
export const heroRank = (s) => {
    const hero = heroChips(s);
    let rank = 1;
    for (let i = 0; i < s.stacks.length; i++)
        if (i !== HERO_INDEX && s.stacks[i] > hero)
            rank++;
    return rank;
};
/** True once hero is at the final table (<= 9 alive) - where every remaining seat is a table-mate. */
export const isFinalTable = (s) => aliveCount(s) <= 9;
/**
 * The still-live prize ladder (pool fractions) for `alive` players given the full `payoutFractions`
 * ladder: the TOP `min(alive, paidPlaces)` prizes remain to be won (the lower prizes were already
 * awarded to the players who busted in the money), padded with zeros to length `alive` so every alive
 * seat has a payout slot for `icmEquity`.
 */
export const livePayouts = (payoutFractions, alive) => {
    if (alive < 1)
        throw new RangeError(`alive must be >= 1, got ${alive}`);
    const paid = Math.min(alive, payoutFractions.length);
    const live = payoutFractions.slice(0, paid);
    while (live.length < alive)
        live.push(0);
    return live;
};
/** The current blind level record. */
export const currentLevel = (s, schedule) => levelAt(schedule, s.level);
export const heroStackContext = (s, schedule) => {
    const level = currentLevel(s, schedule);
    const hero = heroChips(s);
    const biggestRival = s.stacks.reduce((mx, c, i) => (i === HERO_INDEX ? mx : Math.max(mx, c)), 0);
    const effChips = Math.min(hero, biggestRival || hero);
    const effBB = effectiveStackBB(effChips, level.bb);
    const m = mRatio(hero, level);
    return {
        chips: hero,
        effectiveStackBB: effBB,
        m,
        mZone: mZone(m),
        stackStage: stageForStack(effBB),
        band: isPushFoldDepth(effBB) ? nearestPushFoldBand(effBB) : null,
    };
};
/**
 * Price hero's ICM pressure off the LIVE running field - hero vs his biggest rival, against the whole
 * alive field + the live prize ladder. Returns null (chip-EV, pressure `none`) when the field is larger
 * than `ICM_PRICEABLE_MAX`, which is the honest answer deep in a tournament. Reuses the exact same ST1
 * functions (`riskPremium` / `bubbleFactor` / `icmPressureBucket`) the frozen spots use.
 */
export const priceHeroField = (s, payoutFractions, level) => {
    const alive = aliveCount(s);
    if (alive < 2 || alive > ICM_PRICEABLE_MAX)
        return null;
    const hero = heroChips(s);
    let villainIndex = -1;
    let villainChips = -1;
    for (let i = 0; i < alive; i++) {
        if (i === HERO_INDEX)
            continue;
        if (s.stacks[i] > villainChips) {
            villainChips = s.stacks[i];
            villainIndex = i;
        }
    }
    if (villainIndex < 0)
        return null;
    const spot = {
        chipStacks: s.stacks,
        payouts: livePayouts(payoutFractions, alive),
        heroIndex: HERO_INDEX,
        villainIndex,
        atRisk: Math.min(hero, villainChips),
        deadMoney: level.sb + level.bb + level.ante,
    };
    const rp = riskPremium(spot);
    return {
        riskPremium: rp,
        bubbleFactor: bubbleFactor(spot),
        pressure: icmPressureBucket(rp),
        heroCovers: hero > villainChips,
        spot,
    };
};
const totalChips = (stacks) => stacks.reduce((a, b) => a + b, 0);
/**
 * Advance the field by one hero hand: apply hero's own result, run the seeded stack-pressure elimination
 * over the villains (chips conserved via up-flow transfers), and advance the blind level on the hand
 * cadence. Deterministic in (state.seed, state.handNo). Never mutates the input state.
 */
export const advanceField = (s, schedule, heroStep = { deltaChips: 0 }) => {
    const before = totalChips(s.stacks);
    const level = currentLevel(s, schedule);
    const stacks = [...s.stacks];
    // 1) Apply hero's own graded result first, with a conserving counter-transfer.
    let heroBusted = false;
    if (heroStep.deltaChips !== 0) {
        const hero = stacks[HERO_INDEX] + heroStep.deltaChips;
        const cp = heroStep.counterpartyIndex;
        if (cp !== undefined && cp !== HERO_INDEX && cp < stacks.length) {
            // zero-sum against a named rival: the exact chips hero gained/lost move the other way.
            stacks[cp] = stacks[cp] - heroStep.deltaChips;
        }
        else {
            // a blinds/antes bleed (negative) or an uncontested pot (positive): spread the counter across the
            // rest of the field so the invariant holds without inventing a specific rival.
            spreadDelta(stacks, -heroStep.deltaChips, HERO_INDEX);
        }
        stacks[HERO_INDEX] = Math.max(0, hero);
        if (stacks[HERO_INDEX] <= 0)
            heroBusted = true;
    }
    // 2) Seeded elimination over the villains. Fork a per-step RNG so each hand's churn is independent
    //    yet fully reproducible from the run seed + hand number.
    const rng = deriveStepRng(s.seed, s.handNo);
    const alive = stacks.length;
    const bubbleStall = alive > s.paidPlaces && alive - s.paidPlaces <= BUBBLE_STALL_WINDOW;
    const stall = bubbleStall ? BUBBLE_STALL_FACTOR : 1;
    const busts = [];
    for (let i = 0; i < alive; i++) {
        if (i === HERO_INDEX)
            continue; // the field never busts hero
        if (stacks[i] <= 0)
            continue;
        // keep at least a heads-up pair alive: never bust the field below 2 via the model.
        if (alive - busts.length <= 2)
            break;
        const hazard = villainHazard(stacks[i], level) * stall;
        if (rng.next() < hazard)
            busts.push(i);
    }
    // 3) Transfer each busted stack UP to a surviving non-hero rival (weighted by chips), then remove it.
    for (const b of busts) {
        const chips = stacks[b];
        stacks[b] = 0;
        const winner = pickWinner(stacks, rng, b);
        if (winner >= 0)
            stacks[winner] = stacks[winner] + chips;
        else
            stacks[HERO_INDEX] = stacks[HERO_INDEX] + chips; // degenerate: only hero left to hold chips
    }
    const survivors = stacks.filter((c, i) => c > 0 || i === HERO_INDEX);
    // hero (index 0) is never busted by the model, so filtering keeps hero at position 0.
    // 4) Advance the clock + level. Level rises on the hand cadence and is monotonic (never un-raises),
    //    carrying any `startLevel` floor forward via `max(s.level, ...)`.
    const handNo = s.handNo + 1;
    const level2 = Math.min(schedule.levels.length, Math.max(s.level, 1 + Math.floor(handNo / HANDS_PER_LEVEL)));
    const next = {
        ...s,
        stacks: survivors,
        level: level2,
        handNo,
    };
    // Invariant: chips are conserved (a bust transfers, never deletes). Allow a tiny float tolerance.
    const after = totalChips(next.stacks);
    if (Math.abs(after - before) > 1e-6)
        throw new Error(`field.advanceField: chip conservation violated (${before} -> ${after})`);
    return { state: next, bustedThisStep: busts.length, heroBusted };
};
/** Spread `delta` (signed) across every stack except `exceptIndex`, proportional to current chips, so
 *  the total moves by exactly `delta`. Used for hero's blinds bleed / uncontested pot counter-transfer. */
const spreadDelta = (stacks, delta, exceptIndex) => {
    const idxs = stacks.map((_, i) => i).filter((i) => i !== exceptIndex && stacks[i] > 0);
    const pool = idxs.reduce((a, i) => a + stacks[i], 0);
    if (pool <= 0 || idxs.length === 0)
        return;
    let applied = 0;
    for (let k = 0; k < idxs.length; k++) {
        const i = idxs[k];
        const share = k === idxs.length - 1 ? delta - applied : Math.round((stacks[i] / pool) * delta);
        stacks[i] = Math.max(0, stacks[i] + share);
        applied += share;
    }
};
/** Pick a surviving non-hero, non-busted winner to absorb a busted stack, weighted by chips (chips flow
 *  up). Returns -1 if none exists. */
const pickWinner = (stacks, rng, exclude) => {
    const idxs = stacks
        .map((_, i) => i)
        .filter((i) => i !== HERO_INDEX && i !== exclude && stacks[i] > 0);
    if (idxs.length === 0)
        return -1;
    const total = idxs.reduce((a, i) => a + stacks[i], 0);
    let r = rng.next() * total;
    for (const i of idxs) {
        r -= stacks[i];
        if (r <= 0)
            return i;
    }
    return idxs[idxs.length - 1];
};
/** A per-step RNG derived deterministically from the run seed + hand number. Same (seed, handNo) → same
 *  churn, always - the reproducibility TF1 save/resume depends on. */
const deriveStepRng = (seed, handNo) => createRng(`${seed}::field::${handNo}`);
//# sourceMappingURL=field.js.map